// imagenPresets.js — P3-8 «Preparar imágenes». Presets de USO para preparar una
// imagen NO-producto (hero, banner, categoría) y la matemática PURA del recorte por
// preset. El modelo es COVER (recortar a la proporción del preset, sin letterbox),
// distinto del MARGEN del editor de producto (imagenAjuste45, que deja fondo liso).
// Frontend-only → export ES. El canvas real (dibujar + comprimir) vive en el componente.

export const PRESETS_IMAGEN = [
  // P3-12: presets de PRODUCTO por proporción (1600 de ancho; alto según la proporción del tipo).
  // «que pida la proporción»: el usuario elige la del tipo (prenda 4:5, cosmético/consumible 1:1).
  { key: 'producto-4-5', label: 'Producto 4:5 (prenda)', aspectW: 4, aspectH: 5, anchoFijo: 1600, maxKB: 400, ayuda: '4:5 vertical · 1600×2000 · recorta al centro (prendas)' },
  { key: 'producto-1-1', label: 'Producto 1:1 (cosmético/consumible)', aspectW: 1, aspectH: 1, anchoFijo: 1600, maxKB: 400, ayuda: '1:1 cuadrada · 1600×1600 · recorta al centro (cosméticos, consumibles)' },
  { key: 'producto-3-4', label: 'Producto 3:4', aspectW: 3, aspectH: 4, anchoFijo: 1600, maxKB: 400, ayuda: '3:4 vertical · 1600×2133 · recorta al centro' },
  // Hero de escritorio: 16:9. Miguel pidió 1920 ó 2560 (2x) → ancho elegible, 2560 por defecto.
  { key: 'hero-escritorio', label: 'Hero escritorio', aspectW: 16, aspectH: 9, anchos: [2560, 1920], anchoDefecto: 2560, maxKB: 500, ayuda: '16:9 · motivo al centro · 1920 ó 2560 (2x)' },
  // Hero móvil: 2:3 vertical, 1080×1620.
  { key: 'hero-movil', label: 'Hero móvil', aspectW: 2, aspectH: 3, anchoFijo: 1080, maxKB: 400, ayuda: '2:3 vertical · 1080×1620' },
  // Imagen de categoría: 3:4, 900×1200 (lo que ya usa la tienda).
  { key: 'categoria', label: 'Categoría', aspectW: 3, aspectH: 4, anchoFijo: 900, maxKB: 300, ayuda: '3:4 vertical · 900×1200' },
  // Banner libre: conserva la proporción del original; el ancho lo elige el usuario.
  { key: 'banner', label: 'Banner (libre)', aspectW: null, aspectH: null, anchoLibre: true, anchoDefecto: 1920, maxKB: 500, ayuda: 'conserva la proporción original · ancho a elegir' },
];

export function presetPorKey(key) {
  return PRESETS_IMAGEN.find((p) => p.key === key) || PRESETS_IMAGEN[0];
}

// Proporción (ancho/alto) del preset. Banner libre → la del original.
export function proporcionPreset(preset, srcW, srcH) {
  if (preset && preset.aspectW && preset.aspectH) return preset.aspectW / preset.aspectH;
  return (srcW && srcH) ? srcW / srcH : 1;
}

// Dimensiones de SALIDA del preset. `ancho` solo aplica a presets de ancho elegible
// (hero-escritorio) o libre (banner); los fijos lo ignoran.
export function dimsSalida(preset, srcW, srcH, ancho) {
  const ar = proporcionPreset(preset, srcW, srcH);
  // Un preset de ancho FIJO (categoría, hero móvil) ignora el ancho pedido; los de
  // ancho elegible (hero escritorio) o libre (banner) toman el pedido, si no el defecto.
  const w = Math.round(preset.anchoFijo || ancho || preset.anchoDefecto || srcW || 0);
  const h = Math.round(w / ar);
  return { outW: w, outH: h };
}

// Rect del ORIGEN (sx,sy,sw,sh) con la proporción del preset, lo más grande posible a
// zoom=1, centrado en cx,cy; zoom>1 ACERCA (recorta más). Siempre dentro de la imagen.
// Banner libre (sin proporción) → la imagen entera.
export function recortePreset(srcW, srcH, { aspectW, aspectH, zoom = 1, cx = 0.5, cy = 0.5 } = {}) {
  if (!aspectW || !aspectH) return { sx: 0, sy: 0, sw: srcW, sh: srcH };
  const ar = aspectW / aspectH;
  let baseW = srcW;
  let baseH = Math.round(srcW / ar);
  if (baseH > srcH) { baseH = srcH; baseW = Math.round(srcH * ar); }
  const z = Math.min(Math.max(Number(zoom) || 1, 1), 4); // zoom≥1: solo acercar (COVER, sin letterbox)
  const sw = Math.round(baseW / z);
  const sh = Math.round(baseH / z);
  let sx = Math.round(cx * srcW - sw / 2);
  let sy = Math.round(cy * srcH - sh / 2);
  sx = Math.min(Math.max(0, sx), srcW - sw);
  sy = Math.min(Math.max(0, sy), srcH - sh);
  return { sx, sy, sw, sh };
}
